import React, { useRef, useEffect, useState, ReactElement } from 'react'
import omit from 'lodash/omit'
import uniqueId from 'lodash/uniqueId'
import styled from 'styled-components'

import { COLORS, COLORS2, SIZES } from '~/common/globalStyles'
import { MergeTagValueType } from '~/common/types/merge-tags'
import { Label } from '~/views/components/ui/inputs/v2/shared/components/inputElements'
import Text from '~/views/components/ui/Text'
import HelpTooltip from '~/views/components/ui/Tooltip/HelpTooltip'

import InputWrapper from './components/InputWrapper'
import Input from './components/Input'
import TextArea from './components/TextArea'
import type { CSSProperties } from 'react'

export interface TextFieldProps extends React.HTMLProps<HTMLInputElement> {
  className?: string
  disabled?: boolean
  endAdornment?: { margin: number; adornment: React.ReactNode }
  error?: boolean
  formattable?: boolean
  fullWidth?: boolean
  helperText?: React.ReactElement | string
  icon?: React.ReactNode
  id?: string
  InputProps?: Record<string, any>
  inputRef?: (node: HTMLInputElement | null) => void
  internalOnly?: boolean
  label?: string
  mergeTags?: any
  mergeValues?: MergeTagValueType
  multiline?: boolean
  name?: string
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  onClick?: (event: React.MouseEvent<HTMLInputElement>) => void
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void
  placeholder?: string
  readOnly?: boolean
  required?: boolean
  rows?: number
  shrink?: boolean
  startAdornment?: {
    margin: number
    adornment: React.ReactNode
    styles?: CSSProperties
  }
  style?: Record<string, any>
  tooltipText?: string | ReactElement
  tooltipPlacement?: 'bottom' | 'top'
  topHelperText?: string | null
  type?: string
  value?: any
  forceRichEditorReinitialize?: boolean
  dataTestId?: string
}

const AdornmentWrapper = styled.div`
  display: flex;
  align-items: center;
`

const TopTooltipText = styled(Text)`
  position: absolute;
  top: ${SIZES.medium};
  right: 0;
`

const LabelWrapper = styled.div`
  position: relative;
`

const TextFieldV2: React.FC<TextFieldProps> = ({
  className,
  disabled,
  endAdornment,
  formattable,
  helperText,
  icon,
  inputRef,
  internalOnly,
  label,
  multiline,
  required,
  startAdornment,
  tooltipText,
  topHelperText,
  error = false,
  onBlur,
  tooltipPlacement,
  id: inputId,
  dataTestId,
  ...props
}) => {
  const [id] = useState(() => (inputId ? inputId : uniqueId('input_')))
  const internalRef = useRef<HTMLInputElement>(null)
  const [isFocused, setIsFocused] = useState(false)

  useEffect(() => {
    if (inputRef && internalRef) {
      inputRef(internalRef.current)
    }
  }, [inputRef, internalRef])

  const InputComponent = multiline ? TextArea : Input
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const extraProps =
    multiline && !formattable
      ? {
          as: 'textarea' as
            | keyof JSX.IntrinsicElements
            | React.ComponentType<any>,
        }
      : {}

  return (
    <div className={className}>
      {label && (
        <LabelWrapper>
          <Label required={required} htmlFor={id}>
            {label}
          </Label>
          {tooltipText && (
            <HelpTooltip
              title={tooltipText}
              tooltipPlacement={tooltipPlacement}
            />
          )}
          {topHelperText && (
            <TopTooltipText color="lightGray" size="small">
              {topHelperText}
            </TopTooltipText>
          )}
        </LabelWrapper>
      )}
      {
        <InputWrapper
          isFocused={isFocused}
          isDisabled={disabled}
          internalOnly={internalOnly}
        >
          {startAdornment && !multiline && (
            <AdornmentWrapper
              style={{
                marginRight: isNaN(startAdornment.margin)
                  ? SIZES.xxxs
                  : startAdornment.margin,
                ...startAdornment.styles,
              }}
            >
              {startAdornment.adornment}
            </AdornmentWrapper>
          )}
          {icon && icon}
          <InputComponent
            {...omit(props, 'as')}
            {...extraProps}
            disabled={disabled}
            required={required}
            id={id}
            ref={internalRef}
            color={COLORS.darkGray as keyof typeof COLORS}
            onFocus={() => setIsFocused(true)}
            onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
              setIsFocused(false)
              onBlur && onBlur(e)
            }}
            data-testid={dataTestId}
          />
          {endAdornment && !multiline && (
            <AdornmentWrapper
              style={{
                marginLeft: isNaN(endAdornment.margin)
                  ? SIZES.xxxs
                  : endAdornment.margin,
              }}
            >
              {endAdornment.adornment}
            </AdornmentWrapper>
          )}
        </InputWrapper>
      }
      {helperText && (
        <div
          role="alert"
          style={{
            marginTop: SIZES.xs,
            color: error ? COLORS2.red : COLORS2.gray['300'],
          }}
        >
          {helperText}
        </div>
      )}
    </div>
  )
}

/** @component */
export default TextFieldV2
